import React, { useContext } from 'react';
import Box from "@mui/material/Box";
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PieChart } from "@mui/x-charts";
import { UserContext } from '../../../../context/UserContext';
import { formatValue, percentageFormat } from '../../../../helpers/helpers';

const scrollCSS = {
    "&::-webkit-scrollbar": {
        width: "5px",
    },
    "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
}

interface Colour {
    front: string;
    back: string;
}

export interface PieChartData {
    value: number;
    label: string;   
}

export interface ComparedPieChartData {
    label: string;
    value: number;
    comparedValue: number;
}

export interface EposNowPieChartProps {
    data: ComparedPieChartData[];
    currency?: string;
    pieLabels?: string[];
    height?: number;
}

const ComparePieChart = (props: EposNowPieChartProps) => {
    const {
        data,
        currency,
        pieLabels,
        height,
    } = props;
    const theme = useTheme();
    const { locale } = useContext(UserContext);
    const colours: Colour[] = [
        {
            front: theme.palette.blue.main,
            back: theme.palette.blue.light
        },
        {
            front: theme.palette.cyan.main,
            back: theme.palette.cyan.light
        },
        {
            front: theme.palette.teal.main,
            back: theme.palette.teal.light
        },
        {
            front: theme.palette.lightGreen.main,
            back: theme.palette.lightGreen.light
        },
        {
            front: theme.palette.lime.main,
            back: theme.palette.lime.light
        },
        {
            front: theme.palette.yellow.main,
            back: theme.palette.yellow.light
        },
        {
            front: theme.palette.orange.main,
            back: theme.palette.orange.light
        },
        {
            front: theme.palette.deepOrange.main,
            back: theme.palette.deepOrange.light
        },
        {
            front: theme.palette.fucsia.main,
            back: theme.palette.fucsia.light
        },
        {
            front: theme.palette.purple.main,
            back: theme.palette.purple.light
        }
    ];
    const getFrontColour = (index: number) => colours[index % colours.length].front;
    const valueTotal = data?.reduce(((x, y) => x+y.value), 0) || 1;
    const comparedTotal = data?.reduce(((x, y) => x+y.comparedValue), 0) || 1;
    data?.sort((a, b) => b.value - a.value)

    return (
        <Box sx={{ height, display: 'flex', flexDirection: 'column', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {pieLabels && pieLabels.length > 0 &&
                    <Chip
                        size='small'
                        label={pieLabels[0]}
                        sx={{
                            bgcolor: theme.palette.info['190p'],
                            margin: '10px 0px',
                            '& .MuiChip-label': {
                                color: theme.palette.info['160p']
                            }
                        }}
                    />
                }
                {pieLabels && pieLabels.length > 1 &&
                    <Chip
                        size='small'
                        label={pieLabels[1]}
                        sx={{ 
                            bgcolor: theme?.palette?.warning['190p'],
                            margin: '10px 0px',
                            '& .MuiChip-label': {
                                color: theme.palette.warning['160p']
                            }
                        }}
                    />
                }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Box sx={{width: '150px', height: '150px' }}>
                    <PieChart
                        colors={colours.map(c => c.front)}
                        width={150}
                        height={150}
                        slotProps={{ legend: {hidden: true} }}
                        tooltip={{ trigger: 'none' }}
                        series={[{
                            innerRadius: '50%',
                            outerRadius: '100%',
                            startAngle: 20,
                            endAngle: 380,
                            paddingAngle: 0,
                            data: data.map(({comparedValue, ...rest}) => rest),
                        }]}
                        margin={{ right: 0 }}
                    />
                </Box>
                <Box sx={{width: '150px', height: '150px'}}>
                    <PieChart
                        colors={colours.map(c => c.front)}
                        width={150}
                        height={150}
                        slotProps={{ legend: {hidden: true} }}
                        tooltip={{ trigger: 'none' }}
                        series={[{
                            innerRadius: '50%',
                            outerRadius: '100%',
                            startAngle: 20,
                            endAngle: 380,
                            paddingAngle: 0,
                            data: data.map(({comparedValue, ...rest}) => ({...rest, value: comparedValue})),
                        }]}
                        margin={{ right: 0 }}
                    />
                </Box>
            </Box>
            <Box sx={{overflow: 'auto', marginTop: '15px', ...scrollCSS}}>
                {data?.map((item, index) => (
                    <Box key={`${item.label}-${item.value}`} sx={{ margin: '10px 10px 10px 6px' }}>
                        <Box sx={{ borderBottom: 'solid 1px', borderColor: theme.palette.grey['12p'], paddingBottom: '4px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        bgcolor: getFrontColour(index),
                                        marginRight: '8px',
                                    }}
                                />
                                <Typography sx={{ fontSize: '16px', fontWeight: '600', marginRight: '8px' }}>{item.label as string}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent:'space-between', paddingTop: '4px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: '400', marginRight: '8px' }}>
                                    {formatValue(item.value, !!currency, currency, locale)}
                                </Typography>
                                <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>
                                    {percentageFormat(item.value/valueTotal, locale)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: '400', marginRight: '8px' }}>
                                    {formatValue(item.comparedValue, !!currency, currency, locale)}
                                </Typography>
                                <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>
                                    {percentageFormat(item.comparedValue/comparedTotal, locale)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default ComparePieChart;
